// src/components/BundleConfigurator.js
// ─────────────────────────────────────────────────────────────
//  Customer-facing step-through configurator for a BUNDLE item
//  (e.g. Pizza Hut "WOW Triple" = 3 independently customizable pizzas).
//  Steps through each BundleComponent, reusing the existing
//  ModifierSelector (multi-select + min/max validation already built)
//  for that component's own modifier groups.
//
//  CRITICAL: a bundle is ONE cart line — this component collects all
//  component selections, then hands a single configured bundle object
//  back via onAdd. It never adds multiple cart lines.
// ─────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import ModifierSelector from './ModifierSelector';

export default function BundleConfigurator({ item, onAdd, onClose }) {
  const { t, isAr, itemName } = useLanguage();
  const components = [...(item.bundleComponents || [])].sort((a, b) => a.sortOrder - b.sortOrder);

  const [stepIndex,  setStepIndex]  = useState(0);
  const [configured, setConfigured] = useState([]); // [{ componentName, price, modifierNote, selections }]

  if (components.length === 0) {
    // No components defined — degrade to a plain item add (shouldn't normally happen).
    onAdd({ ...item, price: item.price, basePrice: item.price, bundleComponentsDetail: [] });
    onClose();
    return null;
  }

  const current = components[stepIndex];
  const runningTotal = item.price + configured.reduce((s, c) => s + (c.price - (c.basePrice ?? 0)), 0);

  const handleComponentAdd = (configuredComponent) => {
    const detail = {
      componentName: current.displayName,
      price:         configuredComponent.price || 0,
      basePrice:     configuredComponent.basePrice || 0,
      modifierNote:  configuredComponent.modifierNote || '',
      selections:    configuredComponent.selections || {},
    };
    const next = [...configured];
    next[stepIndex] = detail;
    setConfigured(next);

    if (stepIndex < components.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      // Final step — assemble the single bundle cart line
      const addOnTotal = next.reduce((s, c) => s + (c.price - (c.basePrice ?? 0)), 0);
      const finalPrice  = item.price + addOnTotal;
      const summary = next.map(c => `${c.componentName}: ${c.modifierNote || t('No customization', 'بدون تخصيص')}`).join(' | ');

      onAdd({
        ...item,
        price:                  finalPrice,
        basePrice:              item.price,
        modifierNote:           summary,
        bundleComponentsDetail: next,
      });
      onClose();
    }
  };

  // Pseudo-item handed to ModifierSelector for the current component —
  // base price 0 so each component's own modifier price deltas are isolated;
  // BundleConfigurator combines them with the bundle's base price itself.
  const componentItem = {
    id:    current.id,
    name:  current.displayName,
    nameAr: current.displayName,
    emoji: item.emoji,
    price: 0,
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', zIndex:1600, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'var(--bg2)', borderRadius:20, width:'100%', maxWidth:560, border:'1px solid var(--accent)', maxHeight:'92vh', display:'flex', flexDirection:'column', direction: isAr ? 'rtl' : 'ltr' }}>

        {/* Bundle header + progress */}
        <div style={{ padding:'16px 22px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:32 }}>{item.emoji}</span>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:800, fontSize:16 }}>{itemName(item)}</div>
              <div style={{ fontSize:12, color:'var(--accent)', fontWeight:700 }}>
                {t('Step', 'خطوة')} {stepIndex + 1} {t('of', 'من')} {components.length} — {current.displayName}
              </div>
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text3)', fontSize:22, cursor:'pointer' }}>✕</button>
          </div>
          <div style={{ display:'flex', gap:6, marginTop:12 }}>
            {components.map((c, i) => (
              <div key={c.id} style={{
                flex:1, height:6, borderRadius:4,
                background: i < stepIndex ? 'var(--green)' : i === stepIndex ? 'var(--accent)' : 'var(--bg4)',
              }} />
            ))}
          </div>
          <div style={{ marginTop:10, fontSize:13, color:'var(--text2)' }}>
            {t('Running total', 'الإجمالي الجاري')}: <span style={{ color:'var(--accent)', fontWeight:700 }}>AED {runningTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* Reuse ModifierSelector for this component's own groups — embed inline, no its own backdrop.
            closeOnAdd=false: advancing to the next component must NOT close the whole bundle modal
            (handleComponentAdd below decides when the bundle is actually done).
            hideHeader=true: BundleConfigurator's own header above already shows item + step. */}
        <div style={{ position:'relative', flex:1, minHeight:420 }}>
          <InlineModifierSelector
            key={current.id}
            item={componentItem}
            preloadedGroups={current.template?.groups || []}
            onAdd={handleComponentAdd}
            onClose={onClose}
            closeOnAdd={false}
            hideHeader={true}
            submitLabel={stepIndex < components.length - 1
              ? `${t('Next', 'التالي')}: ${components[stepIndex + 1].displayName} →`
              : `🛒 ${t('Add Bundle to Cart', 'إضافة الحزمة إلى السلة')}`}
          />
        </div>
      </div>
    </div>
  );
}

// ModifierSelector renders its own fixed full-screen backdrop + modal — wrap
// it so it lays out inside BundleConfigurator's modal instead of stacking
// another backdrop on top. Same component, same behaviour, different shell.
function InlineModifierSelector(props) {
  return (
    <div style={{ position:'absolute', inset:0 }}>
      <style>{`.bundle-step-inline > div { position:absolute !important; inset:0 !important; background:transparent !important; padding:0 !important; }
        .bundle-step-inline > div > div { max-width:none !important; border:none !important; border-radius:0 !important; height:100% !important; max-height:none !important; }`}</style>
      <div className="bundle-step-inline" style={{ height:'100%' }}>
        <ModifierSelector {...props} />
      </div>
    </div>
  );
}
